import CustomModalWrapper from 'components/CustomModalWrapper/CustomModalWrapper';
import DetailTable from 'components/DetailTable';
import { ConfigItem } from 'components/DetailTable/types';
import LoadingScreen from 'components/LoadingScreen';

import { useAtomValue } from 'jotai';
import { ModuleGuard } from 'components/ModuleGuard';
import { isAdminUserLoggedIn } from 'lib/helper';
import React, { Fragment, useEffect, useState } from 'react';
import { Row, Col, Button, ButtonGroup, Input } from 'reactstrap';
import validator from 'validator';
import { companyIdAtom } from 'views/state';

import { fetchCADetail, parseNetworkList } from './helper';
import PCCompliance from '../../components/PCCompliance/PCCompliance';
import PCComplianceGuardFallback from '../../components/PCCompliance/PCComplianceComingSoon';

type Props = {
  id: number;
};

const CADetailModal = ({ id }: Props) => {
  const [data, setData]: any = useState(null);
  const [searchQuery, setSearchQuery]: any = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredSoftware, setFilteredSoftware] = useState({});
  const companyId = useAtomValue(companyIdAtom);

  useEffect(() => {
    fetchCADetail(id, companyId)
      .then((res) => {
        if (res) {
          const processedData = processJsonData(res);
          setData(processedData);
        }
      })
      .catch((err) => {
        throw err;
      })
      .finally(() => setIsLoading(false));
  }, [id, companyId]);

  const processJsonData = (json: any) => {
    if (!json) return;

    // process IPv6 Address fron network_interface.list
    let ipv6 = '';
    const networkInterfaceList = json['network_interface']['list'];
    if (networkInterfaceList && networkInterfaceList.length) {
      for (const obj of networkInterfaceList) {
        const item = obj['HostAssetInterface'];
        // Check if listChildNode exists
        if (item && item.address) {
          // Check if address property exists and it is an IPv6 address
          if (
            item.address?.includes(':') &&
            validator.isIP(item.address, 4) === false
          ) {
            ipv6 = item.address;
            break;
          }
        }
      }
    }

    // process total_memory covert mb to gb
    const totalMemory = json['total_memory'];
    const processedTotalMemory = totalMemory
      ? `${(totalMemory / 1024).toFixed(2)} GB`
      : null;

    // process volume list covert bytes to gb
    const volumeList = json['volume']['list'];
    const processedVolumeList = volumeList?.map((volumeObj: any) => {
      const volume = volumeObj['HostAssetVolume'];
      const processedVolume = {
        ...volume,
        size:
          volume['size'] && volume['size'] > 0
            ? `${(volume['size'] / 1024 / 1024 / 1024).toFixed(2)} GB`
            : `0.00 GB`,
        free:
          volume['free'] && volume['free'] > 0
            ? `${(volume['free'] / 1024 / 1024 / 1024).toFixed(2)} GB`
            : `0.00 GB`,
      };
      return {
        HostAssetVolume: processedVolume,
      };
    });

    // process software list and sort alphabetically
    const softwareList = JSON.parse(json['software']);
    const sortedSoftwareList = softwareList?.list?.length
      ? softwareList?.list?.sort((a: any, b: any) => {
          const nameA = a['HostAssetSoftware']['name'].toUpperCase();
          const nameB = b['HostAssetSoftware']['name'].toUpperCase();
          if (nameA < nameB) return -1;
          else if (nameA > nameB) return 1;
          else return 0;
        })
      : [];

    setFilteredSoftware({
      software: {
        list: sortedSoftwareList,
      },
    });

    const processedJson = {
      ...json,
      address_ipv6: ipv6,
      total_memory: processedTotalMemory,
      volume: { list: processedVolumeList },
      software: { list: sortedSoftwareList },
    };
    return processedJson;
  };

  if (isLoading && !data) return <LoadingScreen />;

  // assetSummaryTabConfig is the configuration for the Asset Summary tab
  const assetSummaryTabConfig: ConfigItem[] = [
    { name: 'Name', key: 'name', type: 'string' },
    { name: 'Alias', key: 'alias', type: 'string' },
    { name: 'DNS Hostname', key: 'dns_host_name', type: 'string' },
    { name: 'FQDN', key: 'fqdn', type: 'string' },
    { name: 'NetBIOS Name', key: 'netbios_name', type: 'string' },
    { name: 'IPv4 Address', key: 'address', type: 'string' },
    { name: 'IPv6 Addresses', key: 'address_ipv6', type: 'string' },
    { name: 'Asset ID', key: 'asset_id', type: 'string' },
    { name: 'Asset Tagged', key: 'is_tagged', type: 'boolean' },
    { name: 'Qualys ID (Host ID)', key: 'qweb_host_id', type: 'string' },
    { name: 'Last User Login', key: 'lastLoggedOnUser', type: 'string' },
    { name: 'Last System Boot', key: 'last_system_boot', type: 'date' },
    { name: 'Created On', key: 'ca_created', type: 'date' },
    { name: 'Last Checked-In', key: 'last_checked_in', type: 'date' },
    { name: 'Location', key: 'agent_info.location', type: 'string' },
  ];

  // systemInformationTabConfig is the configuration for the System Information tab
  const systemInformationTabConfig: ConfigItem[] = [
    { name: 'Timezone', key: 'timezone', type: 'string' },
    { name: 'Last System Boot', key: 'last_system_boot', type: 'date' },
    { name: 'Manufacturer', key: 'manufacturer', type: 'string' },
    { name: 'Model', key: 'ca_model', type: 'string' },
    { name: 'Total Memory', key: 'total_memory', type: 'string' },
    { name: 'BIOS description', key: 'bios_description', type: 'string' },
    {
      name: 'Volume',
      key: 'volume.list',
      type: 'list',
      listConfig: {
        childNode: 'HostAssetVolume',
        config: [
          { name: 'Free', key: 'free', type: 'string' },
          { name: 'Size', key: 'size', type: 'string' },
          { name: 'Name', key: 'name', type: 'string' },
        ],
      },
    },
    { name: 'Processor', key: 'processor.list', type: 'list' },
    { name: 'Users', key: 'account.list', type: 'list' },
  ];

  // agentSummaryTabConfig is the configuration for the Agent Summary tab
  const agentSummaryTabConfig: ConfigItem[] = [
    { name: 'Agent Id', key: 'agent_info.agentId', type: 'string' },
    { name: 'Agent Version', key: 'agent_info.agentVersion', type: 'string' },
    {
      name: 'Activated Modules',
      key: 'agent_info.activatedModule',
      type: 'string',
    },
    {
      name: 'Manifest Last Processed',
      key: 'agent_info.manifestVersion.vm',
      type: 'string',
    },
    { name: 'Operating System', key: 'os', type: 'string' },
    {
      name: 'Activation Key',
      key: 'agent_info.activationKey.activationId',
      type: 'string',
    },
    { name: 'Status', key: 'agent_info.chirpStatus', type: 'string' },
    { name: 'Connected From', key: 'agent_info.connectedFrom', type: 'string' },
    { name: 'Last Checked-In', key: 'agent_info.lastCheckedIn', type: 'date' },
    {
      name: 'Configuration Profile',
      key: 'agent_info.agentConfiguration.name',
      type: 'string',
    },
  ];

  // networkInformationTabConfig is the configuration for the Network Information tab
  const networkInformationTabConfig: ConfigItem[] = [
    { name: '', key: 'network_interface.list', type: 'list' },
  ];

  // openPortsTabConfig is the configuration for the Open Ports tab
  const openPortsTabConfig: ConfigItem[] = [
    { name: '', key: 'open_port.list', type: 'list' },
  ];

  // installedSoftwareTabConfig is the configuration for the Installed Software tab
  const installedSoftwareTabConfig: ConfigItem[] = [
    { name: '', key: 'software.list', type: 'list' },
  ];

  const isAdminUser = isAdminUserLoggedIn();

  // tabs is the configuration for the tabs which are rendered
  const tabs = [
    {
      title: 'Asset Summary',
      config: assetSummaryTabConfig,
      data: data,
    },
    {
      title: 'System Information',
      config: systemInformationTabConfig,
      data: data,
    },
    {
      title: 'Agent Summary',
      config: agentSummaryTabConfig,
      data: data,
    },
    {
      title: 'Network Information',
      config: networkInformationTabConfig,
      data: parseNetworkList(data?.network_interface?.list),
    },
    // for now show only to admin users, later we must respect the tenant level settings
    // and check if PC module is enabled for the tenant
    {
      title: 'Compliance',
      contentsComponent: (
        <ModuleGuard
          module="pcm"
          fallback={<PCComplianceGuardFallback isCompact />}
        >
          <PCCompliance
            id={data.host_id}
            compliance_data={data.policy_compliance}
          />
        </ModuleGuard>
      ),
    },
    {
      title: 'Open Ports',
      config: openPortsTabConfig,
      data: data,
    },
    {
      title: 'Installed Software',
      config: installedSoftwareTabConfig,
      data: filteredSoftware,
    },
  ];

  function handleSearch(value: string): void {
    const filteredSoftware = data.software.list.filter((item) =>
      item.HostAssetSoftware.name.toLowerCase().includes(value.toLowerCase()),
    );
    setFilteredSoftware({
      software: {
        list: filteredSoftware,
      },
    });
  }

  return (
    <CustomModalWrapper overflowY="hidden" id="main-wrapper">
      {data && (
        <Fragment>
          <Row>
            <Col sm="6" md="4" lg="3">
              <ButtonGroup
                vertical
                className="btn-group-toggle"
                style={{ maxWidth: '100%', width: '100%' }}
              >
                {tabs.map((tab, index) => (
                  <Button
                    key={`tab-btn-${index}`}
                    tag="label"
                    style={{
                      padding: '10px',
                      margin: '0px',
                      textAlign: 'left',
                    }}
                    color={activeTab === index ? 'primary' : 'secondary'}
                    size="sm"
                    title={tab.title}
                    onClick={() => setActiveTab(index)}
                  >
                    {tab.title}
                  </Button>
                ))}
              </ButtonGroup>
            </Col>
            <Col sm="6" md="8" lg="9">
              {activeTab === 6 && (
                <Input
                  className="mb-2"
                  name="search"
                  maxLength={255}
                  placeholder="Search"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchQuery(value);
                    handleSearch(value);
                  }}
                />
              )}
              <CustomModalWrapper id="scroll-wrapper">
                {tabs.map(
                  (tab, index) =>
                    activeTab === index &&
                    (tab.config ? (
                      <DetailTable
                        key={index}
                        data={tab.data}
                        config={tab.config as ConfigItem[]}
                      />
                    ) : (
                      tab.contentsComponent
                    )),
                )}
              </CustomModalWrapper>
            </Col>
          </Row>
        </Fragment>
      )}
    </CustomModalWrapper>
  );
};

export default CADetailModal;
